import React, { FC, useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BackArrow, Tabs, TopProgress } from '../../../components'
import { staffObjectStorageService } from '../../../api'
import { StaffObjectUserDetails } from '../../../api/staffObjectStorageService'
import { usePageTitle } from '../../../context/navigationContext'
import { useUi } from '../../../context/ui.context'
import ObjectUserOverview from './user-overview'
import ObjectUserTokens from './user-tokens'
import {
  DetailHeader,
  HeaderText,
  MutedText,
  Navigation,
  PageContainer,
  PageTitle,
  StatusBadge,
} from './styles'
import { getObjectStorageError } from './utils'

const ObjectUserPage: FC<{ userListPath: string }> = ({ userListPath }) => {
  const [t] = useTranslation()
  const { objectUserId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { viewAlert } = useUi()
  const [objectUser, setObjectUser] = useState<StaffObjectUserDetails | null>(null)
  const [pageLoading, setPageLoading] = useState(true)
  const lastPathPart = location.pathname.split('/').filter(Boolean).pop()
  const activeTab = ['overview', 'tokens'].includes(lastPathPart || '') ? lastPathPart : 'overview'

  usePageTitle(objectUser
    ? `${t('PAGE_TITLE_OBJECT_STORAGE_USER')} - ${objectUser.ceph_uid || objectUser.id}`
    : t('PAGE_TITLE_OBJECT_STORAGE_USER'))

  const loadUser = () => {
    if (!objectUserId) return
    staffObjectStorageService.getUser(objectUserId)
      .then(({ data }) => setObjectUser(data))
      .catch((error) => {
        viewAlert({
          severity: 'error',
          message: getObjectStorageError(error, t('ERROR_OCCURRED')),
        })
        navigate(userListPath)
      })
      .finally(() => setPageLoading(false))
  }

  useEffect(() => {
    loadUser()
  }, [objectUserId])

  if (pageLoading) return <TopProgress loading />
  if (!objectUser || !objectUserId) return null

  const tabs = [
    { id: '1', title: t('OVERVIEW'), slug: 'overview' },
    { id: '2', title: `${t('S3_KEYS')} (${objectUser.keys_count})`, slug: 'tokens' },
  ]
  const active = !objectUser.deleted && !objectUser.disabled

  return (
    <PageContainer>
      <BackArrow fallbackPath={userListPath} />
      <DetailHeader>
        <HeaderText>
          <PageTitle>{objectUser.ceph_uid || objectUser.id}</PageTitle>
          <MutedText>{objectUser.client.display || objectUser.client.name}</MutedText>
          <StatusBadge $active={active}>
            {objectUser.deleted
              ? t('DELETED')
              : objectUser.disabled ? t('DISABLED') : t('ACTIVE')}
          </StatusBadge>
        </HeaderText>
      </DetailHeader>
      <Navigation>
        <div>
          <Tabs
            tabs={tabs}
            size="display"
            location={activeTab}
            blockNavigate
            onChange={(tab) => navigate(`${userListPath}/${objectUserId}/${tab}`)}
          />
        </div>
      </Navigation>
      <Routes>
        <Route path="/" element={<Navigate replace to="overview" />} />
        <Route
          path="/overview"
          element={<ObjectUserOverview objectUser={objectUser} onUpdate={setObjectUser} />}
        />
        <Route path="/tokens" element={<ObjectUserTokens objectUserId={objectUserId} />} />
        <Route path="*" element={<Navigate replace to="overview" />} />
      </Routes>
    </PageContainer>
  )
}

export default ObjectUserPage
